import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { ArrowLeft, Filter, ChevronDown } from 'lucide-react'
import { Button } from '../components/ui/button'
import BlogPost from '../components/BlogPost'
import { Post, Category } from '../types'
import { api } from '../services/api'

const BlogPage: React.FC = () => {
  const [allPosts, setAllPosts] = useState<Post[]>([])
  const [filteredPosts, setFilteredPosts] = useState<Post[]>([])
  const [categories, setCategories] = useState<Category[]>([])
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null)
  const [showFilterDropdown, setShowFilterDropdown] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true)
        setError(null)

        const [postsRes, categoriesRes] = await Promise.all([
          api.get('/posts'),
          api.get('/categories')
        ])

        const posts = Array.isArray(postsRes.data) ? postsRes.data : []
        const categories = Array.isArray(categoriesRes.data) ? categoriesRes.data : []
        
        setAllPosts(posts)
        setFilteredPosts(posts)
        setCategories(categories)
      } catch (err: any) {
        console.error('Error fetching blog data:', err)
        setError(err.response?.data?.error || 'Failed to load blog posts')
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showFilterDropdown && !(event.target as Element).closest('.filter-dropdown')) {
        setShowFilterDropdown(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [showFilterDropdown])

  const getCategoryForPost = (categoryId: string) => {
    return categories.find(cat => cat.id === categoryId)
  }

  const handleFilterChange = (categoryId: string | null) => {
    setSelectedCategory(categoryId)
    if (categoryId === null) {
      setFilteredPosts(allPosts)
    } else {
      setFilteredPosts(allPosts.filter(post => post.category_id === categoryId))
    }
  }

  const getSelectedCategoryName = () => {
    if (!selectedCategory) return 'All Categories'
    const category = categories.find(cat => cat.id === selectedCategory)
    return category?.name || 'All Categories'
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-excel-green mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading blog posts...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Error Loading Blog</h1>
          <p className="text-lg text-gray-600 mb-8">{error}</p>
          <Link to="/">
            <Button variant="excel">Return Home</Button>
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="container mx-auto px-4 py-8">
          <div className="flex items-center mb-4">
            <Link to="/">
              <Button variant="ghost" className="mr-4">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Home
              </Button>
            </Link>
          </div>
          
          <div className="flex flex-col md:flex-row md:items-center md:justify-between">
            <div>
              <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
                All Blog Posts
              </h1>
              <p className="text-xl text-gray-600 max-w-2xl">
                Explore all our Excel tutorials, tips, and insights in one place
              </p>
            </div>
            
            <div className="mt-4 md:mt-0 flex items-center">
              <span className="text-sm text-gray-500 mr-4">
                {filteredPosts.length} {filteredPosts.length === 1 ? 'article' : 'articles'}
              </span>
              <div className="relative filter-dropdown">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setShowFilterDropdown(!showFilterDropdown)}
                >
                  <Filter className="h-4 w-4 mr-2" />
                  {getSelectedCategoryName()}
                  <ChevronDown className="h-4 w-4 ml-2" />
                </Button>
                
                {showFilterDropdown && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg border z-50">
                    <div className="py-1">
                      <div className="px-4 py-2 text-sm font-medium text-gray-700 border-b">
                        Filter by Category
                      </div>
                      <button
                        onClick={() => {
                          handleFilterChange(null)
                          setShowFilterDropdown(false)
                        }}
                        className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${
                          selectedCategory === null ? 'bg-gray-100' : ''
                        }`}
                      >
                        All Categories
                      </button>
                      {categories.map((category) => (
                        <button
                          key={category.id}
                          onClick={() => {
                            handleFilterChange(category.id)
                            setShowFilterDropdown(false)
                          }}
                          className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${
                            selectedCategory === category.id ? 'bg-gray-100' : ''
                          }`}
                        >
                          {category.name}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Posts Grid */}
      <div className="container mx-auto px-4 py-12">
        {filteredPosts.length === 0 ? (
          <div className="text-center py-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              {selectedCategory ? 'No Posts in This Category' : 'No Posts Found'}
            </h2>
            <p className="text-gray-600 mb-6">
              {selectedCategory 
                ? `There are no posts in "${getSelectedCategoryName()}" category yet.`
                : 'There are no blog posts available at the moment.'
              }
            </p>
            {selectedCategory ? (
              <Button variant="outline" onClick={() => handleFilterChange(null)}>
                View All Posts
              </Button>
            ) : (
              <Link to="/">
                <Button variant="excel">Back to Home</Button>
              </Link>
            )}
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredPosts.map((post) => {
              const category = getCategoryForPost(post.category_id)
              return (
                <BlogPost 
                  key={post.id} 
                  post={post} 
                  category={category}
                />
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}

export default BlogPage